import React, { useEffect, useState } from 'react';
import { useBulkReportStore } from '../../store/bulkReportStore';
import { BulkReportStatus } from '../../models/bulk_report';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import PageHeader from '../../components/common/PageHeader';
import Badge from '../../components/common/Badge';
import Notice from '../../components/common/Notice';
import EmptyState from '../../components/common/EmptyState';
import LoadingMessage from '../../components/common/LoadingMessage';
import CreateBulkReportDialog from '../../components/common/CreateBulkReportDialog';
import { useToast } from '../../components/common/Toast';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { LuDownload, LuFiles, LuLoaderCircle, LuPlus, LuRefreshCw, LuTrash2 } from 'react-icons/lu';
import useSchoolStore from '../../store/schoolStore';
import { getClassName } from '../../utils/schoolUtils';
import  useUserStore  from '../../store/userStore';

const BulkReportPage = () => {
  const { showToast, ToastComponent } = useToast();
  const { 
    bulkReports, 
    isBulkReportLoading, 
    isBulkReportCreating,
    error, 
    deletingReportIds,
    loadingErrorItemsReportIds,
    errorItemsByReportId,
    getBulkReports,
    deleteBulkReport,
    getReportErrorItems 
  } = useBulkReportStore();
  const { schoolId } = useSchoolStore();      

  const {isLoading: isUserLoading, loginError} = useUserStore();
  
  const [refreshing, setRefreshing] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  useEffect(() => {
    // Fetch bulk reports on component mount
    if(!isUserLoading){
      getBulkReports();
    }
  }, [isUserLoading]);

  // Show toast for store errors
  useEffect(() => {
    if (error || loginError) {
      showToast(error || loginError, 'error');
    }
  }, [error,loginError, showToast]);

  const handleRefresh = async () => {
    setRefreshing(true);
    const result = await getBulkReports();
    setRefreshing(false);
    
    // // Show toast if refresh failed
    // if (result && !result.success) {
    //   showToast(result.message || 'Failed to refresh bulk reports', 'error');
    // }
  };

//   const handleCreateSuccess = () => {
//     // Refresh the reports list after successful creation
//     handleRefresh();
//   };

  const handleDeleteConfirmed = async () => {
    const reportId = pendingDeleteId;
    setPendingDeleteId(null);
    const result = await deleteBulkReport(reportId);
    if (result && !result.success) {
      showToast(result.message || 'Failed to delete bulk report', 'error');
    } else if (result && result.success) {
      showToast('Bulk report deleted successfully', 'success');
    }
  };

  
  const handleDownload = (zipUrl, reportTitle) => {
    if (zipUrl) {
      const link = document.createElement('a');
      link.href = zipUrl;
      link.download = `${reportTitle.replace(/\s+/g, '_')}.zip`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // A finished report with some failed students still counts as completed (see getStatusText)
  const getStatusTone = (status) => {
    switch (status) {
      case BulkReportStatus.COMPLETED:
      case BulkReportStatus.FAILED:
        return 'green';
      case BulkReportStatus.IN_PROGRESS:
        return 'amber';
      default:
        return 'grey';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    // Since the BulkReport model now converts timestamps, 
    // we should receive a proper Date object
    if (date instanceof Date) {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    // Fallback for any unconverted timestamps
    if (typeof date === 'string') {
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    }
    
    return 'N/A';
  };

  const getStatusText = (status) => {
    switch (status) {
      case BulkReportStatus.COMPLETED:
        return 'Completed';
      case BulkReportStatus.IN_PROGRESS:
        return 'In Progress';
      case BulkReportStatus.FAILED:
        return 'Completed';
      default:
        return 'Unknown';
    }
  }

  const getProgressPercentage = (processedStudents, totalStudents) => {
    if (!totalStudents || totalStudents === 0) return 0;
    return Math.round((processedStudents / totalStudents) * 100);
  };

  const isPageLoading = isBulkReportLoading || isBulkReportCreating || isUserLoading;

  const renderReport = (report) => {
    const isDeleting = deletingReportIds.includes(report.id);
    const isLoadingErrorItems = loadingErrorItemsReportIds?.includes(report.id);
    const progressPercentage = getProgressPercentage(report.processedStudents, report.totalStudents);
    const classNames = report.classList
      ? report.classList.map(item => `${getClassName(schoolId, item.Class)} ${item.sec}`).join(', ')
      : '—';

    return (
      <Card key={report.id}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-[15px] font-semibold text-ui-ink">{report.title || 'Untitled report'}</h3>
              <Badge tone={getStatusTone(report.status)}>
                {report.status === BulkReportStatus.IN_PROGRESS && <LuLoaderCircle aria-hidden className="size-3 animate-spin" />}
                {getStatusText(report.status)}
              </Badge>
            </div>
            {report.description && <p className="mt-1 text-sm text-ui-ink-2">{report.description}</p>}
            <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <div className="flex gap-1.5">
                <dt className="text-ui-ink-3">Classes</dt>
                <dd className="text-ui-ink">{classNames}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt className="text-ui-ink-3">Created</dt>
                <dd className="text-ui-ink">{formatDate(report.createdAt)}</dd>
              </div>
              {report.itemCount && (
                <div className="flex gap-1.5">
                  <dt className="text-ui-ink-3">Reports</dt>
                  <dd className="text-ui-ink tabular-nums">{report.itemCount}</dd>
                </div>
              )}
              {report.updatedAt && (
                <div className="flex gap-1.5">
                  <dt className="text-ui-ink-3">Last updated</dt>
                  <dd className="text-ui-ink">{formatDate(report.updatedAt)}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {report.zipUrl && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleDownload(report.zipUrl, report.title)}
                leftIcon={<LuDownload className="size-4" />}
              >
                Download
              </Button>
            )}
            {report.status !== BulkReportStatus.IN_PROGRESS && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  setPendingDeleteId(report.id);
                }}
                loading={isDeleting}
                leftIcon={<LuTrash2 className="size-4" />}
                className="text-red-700 hover:bg-red-50 hover:text-red-800 dark:text-red-300 dark:hover:bg-red-500/10"
              >
                Delete
              </Button>
            )}
          </div>
        </div>

        {report.status === BulkReportStatus.IN_PROGRESS && report.totalStudents > 0 && (
          <div className="mt-4">
            <div className="mb-1.5 flex items-center justify-between text-sm">
              <span className="text-ui-ink-2">Making reports</span>
              <span className="text-ui-ink tabular-nums">
                {report.processedStudents || 0} of {report.totalStudents} students ({progressPercentage}%)
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-ui-muted" aria-hidden>
              <div className="h-full rounded-full bg-brand-500" style={{ width: `${progressPercentage}%` }} />
            </div>
          </div>
        )}

        {report.status === BulkReportStatus.FAILED && report.error && (
          <Notice
            tone="warning"
            title="Some reports could not be made"
            className="mt-4"
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => getReportErrorItems(report.id)}
                loading={isLoadingErrorItems}
              >
                {isLoadingErrorItems ? 'Loading…' : 'See which ones'}
              </Button>
            }
          >
            {report.error}
            {errorItemsByReportId?.[report.id] && (
              <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-ui-card p-2 text-xs break-words whitespace-pre-wrap text-ui-ink">
                {errorItemsByReportId[report.id]}
              </pre>
            )}
          </Notice>
        )}
      </Card>
    );
  };

  const renderBody = () => {
    if (isPageLoading) {
      return (
        <Card>
          <LoadingMessage message={isBulkReportCreating ? 'Starting the bulk report…' : 'Loading bulk reports…'} />
        </Card>
      );
    }

    if (!bulkReports || bulkReports.length === 0) {
      return (
        <Card>
          <EmptyState
            icon={<LuFiles />}
            title="No bulk reports yet"
            message="A bulk report makes report cards for every student of the classes you choose, as one download."
            action={
              <Button onClick={() => setShowCreateDialog(true)} leftIcon={<LuPlus className="size-4" />}>
                Create bulk report
              </Button>
            }
          />
        </Card>
      );
    }

    return <div className="flex flex-col gap-4">{bulkReports.map(renderReport)}</div>;
  };

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Bulk reports"
        subtitle="Report cards for whole classes at once. Reports take a few minutes; press Refresh to see the latest progress."
        actions={
          <>
            <Button variant="secondary" onClick={handleRefresh} loading={refreshing} leftIcon={<LuRefreshCw className="size-4" />}>
              Refresh
            </Button>
            <Button onClick={() => setShowCreateDialog(true)} leftIcon={<LuPlus className="size-4" />}>
              Create bulk report
            </Button>
          </>
        }
      />

      {renderBody()}

      <CreateBulkReportDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
      />

      <ConfirmDialog
        isOpen={pendingDeleteId !== null}
        title="Delete this bulk report?"
        message="The report and its download will be removed. This cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDeleteId(null)}
      />

      {ToastComponent}
    </div>
  );
};

export default BulkReportPage;
